import React, { useEffect, useState } from 'react';
import { useAtomValue as useStateSegmentValue } from 'jotai';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { RiFileForbidLine } from '@remixicon/react';
import { translate } from '../../../../localization';
import styles from './IndicationsTab.styles.module.css';
import {
  BrandDetails as BrandDetailsType,
  knowledgeCenterStateSegment,
  Indication,
} from '../../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { TooltipPosition } from '../../InfoTooltip';
import BrandsInfoTable from '../../BrandsInfoTable';
import InfoLogo from '../InfoLogo';
import Loading from '../../Loading';
import Button from '../../../atoms/Button';
import BrandsMessage from '../BrandsMessage';
import Retry from '../Retry';
import useBrandMessageState from '../../../../hooks/brandInfoPage/useBrandMessageState';
import { INCLUDES_PARAMS } from '../../../../constants/common';
import { fetchBrandDetails } from '../../../../store/markettingContentGeneration/actions/majorProcessActions';
import processIndicationsArray from '../../../../utils/processIndicationsArray';

const IndicationsTab: React.FC = () => {
  const [retryCount, setRetryCount] = useState(0);

  const { brandDetails, apiPayloadUsed } = useStateSegmentValue(
    knowledgeCenterStateSegment
  );

  const { isLoading, errorMessage } = brandDetails || {};

  const brandDetailsData = get(brandDetails, 'data', {} as BrandDetailsType);
  const name = get(brandDetailsData, 'name', '');
  const indications = get(brandDetailsData, 'indications', [] as Indication[]);

  const {
    state: brandMessageConfig,
    updateScenarioNoData,
    updateScenarioTabLevelError,
    updateScenarioTabLevelApiDown,
  } = useBrandMessageState();

  const columnsConfiguration = [
    {
      title: translate('default_name_column_header'),
      sortable: true,
      showInfoIcon: true,
      infoText: translate('default_name_tooltip'),
      value: 'name',
      tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
    },
    {
      title: translate('full_name_column_header'),
      sortable: false,
      showInfoIcon: true,
      infoText: translate('full_name_tooltip'),
      value: 'longName',
      tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
    },
    {
      title: translate('abbreviated_name_column_header'),
      sortable: false,
      showInfoIcon: true,
      infoText: translate('abbreviated_name_tooltip'),
      value: 'shortName',
    },
    {
      title: translate('inclusion_keywords_column_header'),
      sortable: false,
      showInfoIcon: true,
      infoText: translate('inclusion_keywords_tooltip'),
      tooltipPosition: TooltipPosition.BOTTOM_RIGHT,
      value: 'inclusions',
    },
    {
      title: translate('exclusion_keywords_column_header'),
      sortable: false,
      showInfoIcon: true,
      infoText: translate('exclusion_keywords_tooltip'),
      tooltipPosition: TooltipPosition.BOTTOM_LEFT,
      value: 'exclusions',
    },
  ];

  const handleTabLevelRetry = async () => {
    const brandsApiPayload = {
      ...apiPayloadUsed,
      includes: INCLUDES_PARAMS,
    };

    const success = await fetchBrandDetails(brandsApiPayload);

    if (success) {
      setRetryCount(0);
    } else {
      setRetryCount(prev => prev + 1);
    }
  };

  useEffect(() => {
    if (!isLoading && errorMessage?.length === 0 && isEmpty(indications)) {
      updateScenarioNoData({
        logo: <RiFileForbidLine size={64} color='#A1A1AA' />,
      });
      // if there is no data for the tab, then this useEffect will handle it
    }
  }, [isLoading, indications]);

  useEffect(() => {
    if (errorMessage?.length > 0) {
      updateScenarioTabLevelError(
        <InfoLogo />,
        <Button
          type='button'
          size='m'
          variant='outline'
          colorScheme='#0000C9'
          onClick={handleTabLevelRetry}
        >
          <Retry />
        </Button>
      );
    }
  }, [errorMessage?.length]);

  useEffect(() => {
    if (retryCount > 3) {
      updateScenarioTabLevelApiDown(<InfoLogo />);
    }
  }, [retryCount]);

  if (isLoading) {
    return (
      <div className={styles.indications__loading}>
        <Loading />
      </div>
    );
  }

  if (!isLoading && (isEmpty(indications) || errorMessage?.length > 0)) {
    const { cta, logo, message, title } = brandMessageConfig || {};

    return (
      <div className={styles.indications__loading}>
        <BrandsMessage logo={logo} title={title} message={message} cta={cta} />
      </div>
    );
  }

  return (
    <>
      <div className={styles.indications_tab__indications_title}>
        {name && name != null
          ? `${translate('indications_for')} ${name}`
          : translate('marketingbox_indications_label')}
      </div>

      <BrandsInfoTable
        columnsConfiguration={columnsConfiguration}
        tableData={processIndicationsArray(indications)}
        gridColumnsStyle='15% 20% 15% 25% 25%'
      />
    </>
  );
};

export default IndicationsTab;
